import { useEffect, useState } from "react";
import { ApplePaySessionClass } from "@/lib/misc/ApplePaySessionClass";

export enum ApplePayCapability {
  Unknown,
  CannotMakePayments,
  CanMakePayments,
  CanMakePaymentsWithActiveCard,
  CanMakePaymentsWithoutActiveCard,
}

interface MerchantCapability {
  merchantIdentifier: string | undefined;
  capability: ApplePayCapability;
}

function canMakePaymentsSafely() {
  try {
    return ApplePaySessionClass?.canMakePayments();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(e);
    return false;
  }
}

export const useApplePayCapability = (
  merchantIdentifier: string | undefined,
  options: { checkForActiveCard: boolean } = { checkForActiveCard: false }
): ApplePayCapability => {
  const [
    merchantCapability,
    setMerchantCapability,
  ] = useState<MerchantCapability>({
    merchantIdentifier,
    capability: ApplePayCapability.Unknown,
  });

  useEffect(() => {
    if (merchantIdentifier !== merchantCapability.merchantIdentifier) {
      setMerchantCapability({
        merchantIdentifier,
        capability: ApplePayCapability.Unknown,
      });
    } else if (merchantIdentifier === undefined) {
      return;
    } else if (merchantCapability.capability === ApplePayCapability.Unknown) {
      (async () => {
        setMerchantCapability({
          merchantIdentifier,
          capability: canMakePaymentsSafely()
            ? ApplePayCapability.CanMakePayments
            : ApplePayCapability.CannotMakePayments,
        });
      })();
    } else if (
      merchantCapability.capability === ApplePayCapability.CanMakePayments &&
      options.checkForActiveCard
    ) {
      (async () => {
        setMerchantCapability({
          merchantIdentifier,
          capability: (await ApplePaySessionClass?.canMakePaymentsWithActiveCard(
            merchantIdentifier
          ))
            ? ApplePayCapability.CanMakePaymentsWithActiveCard
            : ApplePayCapability.CanMakePaymentsWithoutActiveCard,
        });
      })();
    }
  }, [merchantIdentifier, options.checkForActiveCard, merchantCapability]);

  return merchantCapability.merchantIdentifier === merchantIdentifier
    ? merchantCapability.capability
    : ApplePayCapability.Unknown;
};
